import {
  ArrowForward,
  Cancel,
  PauseCircleFilled,
  PlayCircle,
  RestartAlt,
  History,
} from '@mui/icons-material';
import {
  Box,
  Grid,
  Step,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../common/enums/enums';
import { fromDBToLocalTimestamp } from '../helpers/date';
import ActivityLogContentStructure from './ActivityLogContentStructure';
import GenButton from './GenButton';
import GeneralModal from './Modal';
import {
  CancelModal,
  PauseModal,
} from './WorkOrderStepperModals/CancelPauseModal';
import RestoreModal from './WorkOrderStepperModals/RestoreModal';
import ResumeModal from './WorkOrderStepperModals/ResumeModal';

const PREFIX = 'WorkOrderStepper';

const classes = {
  generated: `${PREFIX}-generated`,
  approved: `${PREFIX}-approved`,
  inTransit: `${PREFIX}-inTransit`,
  arrived: `${PREFIX}-arrived`,
  inProcess: `${PREFIX}-inProcess`,
  done: `${PREFIX}-done`,
  canceled: `${PREFIX}-canceled`,
  paused: `${PREFIX}-paused`,
  declined: `${PREFIX}-declined`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.generated}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.primary.light,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },
  [`& .${classes.approved}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.primary.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },
  [`& .${classes.inTransit}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.primary.dark,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.arrived}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: '#0D3C61',
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.inProcess}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.warning.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.done}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.success.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.canceled}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.error.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.paused}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.warning.dark,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.declined}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.error.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },
}));

const stepsFullObject = [
  {
    step: ProcessStep.GENERATED,
    createdAt: null,
    person: null,
  },
  {
    step: ProcessStep.APPROVED,
    createdAt: null,
    person: null,
  },
  {
    step: ProcessStep.IN_TRANSIT,
    createdAt: null,
    person: null,
  },
  {
    step: ProcessStep.ARRIVED,
    createdAt: null,
    person: null,
  },
  {
    step: ProcessStep.IN_PROCESS,
    createdAt: null,
    person: null,
  },
  {
    step: ProcessStep.DONE,
    createdAt: null,
    person: null,
  },
];

const getStepsFromLastCreatedOrInitialSteps = steps => {
  if (!steps?.length) return null;
  const lastGeneratedIndex = Math.max(
    ...steps.map((s, i) => (s.step === ProcessStep.GENERATED ? i : -1))
  );
  return lastGeneratedIndex >= 0 ? steps.slice(lastGeneratedIndex) : steps;
};
const isCanceled = steps => steps.some(s => s.step === ProcessStep.CANCELED);

const hasBeenPaused = steps => steps.some(s => s.step === ProcessStep.PAUSED);

const isPaused = steps => steps[steps.length - 1].step === ProcessStep.PAUSED;

const isDeclined = steps =>
  steps[steps.length - 1].step === ProcessStep.DECLINED;

const getDeclinedIndex = steps => {
  const stepBeforeDecline = steps[steps.length - 2].step;
  const approvalStepIndex = 1;
  if (stepBeforeDecline === ProcessStep.GENERATED) return approvalStepIndex;

  return stepsFullObject.map(e => e.step).indexOf(stepBeforeDecline);
};

const isRestoredOrderLastStepEqualTo = (steps, step) => {
  const itsBeenOnInProcessMultipleTimes =
    steps?.filter(s => s.step === ProcessStep.IN_PROCESS).length > 1;
  const isLastStepEqualToStep = steps[steps?.length - 1]?.step === step;

  return itsBeenOnInProcessMultipleTimes && isLastStepEqualToStep;
};

const isRestored = steps =>
  isRestoredOrderLastStepEqualTo(steps, ProcessStep.IN_PROCESS);

const hasBeenRestored = steps =>
  isRestoredOrderLastStepEqualTo(steps, ProcessStep.DONE);

const printedStepsModifier = steps => {
  const obj = [...stepsFullObject];
  const isDone = steps[steps?.length - 1]?.step === ProcessStep.DONE;
  isCanceled(steps) &&
    !isDone &&
    obj.splice(-1, 1, {
      step: ProcessStep.CANCELED,
      createdAt: null,
      person: null,
    });

  hasBeenPaused(steps) &&
    obj.splice(2, 1, {
      step: ProcessStep.PAUSED,
      createdAt: null,
      person: null,
    });

  isDeclined(steps) &&
    obj.splice(getDeclinedIndex(steps), 1, {
      step: ProcessStep.DECLINED,
      createdAt: null,
      person: null,
    });
  return obj;
};

const pausedStatesHandler = steps => {
  if (isPaused(steps)) {
    return steps.filter(
      s =>
        s.step === ProcessStep.GENERATED ||
        s.step === ProcessStep.APPROVED ||
        s.step === ProcessStep.PAUSED
    );
  }

  if (hasBeenPaused(steps)) {
    return steps
      .filter(
        s => s.step === ProcessStep.GENERATED || s.step === ProcessStep.APPROVED
      )
      .concat(
        ...steps.slice(
          Math.max(
            ...steps.map((s, i) => (s.step === ProcessStep.PAUSED ? i : -1))
          )
        )
      );
  }
  return steps;
};

const restoreStatesHandler = steps => {
  const getProcessStepIndex = processStep =>
    steps?.map(s => s.step)?.indexOf(processStep);

  if (isRestored(pausedStatesHandler(steps))) {
    const lastInProcess = steps.slice(-1);
    const cleanSteps = steps.slice(
      0,
      getProcessStepIndex(ProcessStep.IN_PROCESS)
    );
    cleanSteps.push(...lastInProcess, {
      step: ProcessStep.DONE,
      createdAt: null,
      person: null,
    });
    return cleanSteps;
  }
  if (hasBeenRestored(steps)) {
    const lastTwoValues = steps.slice(-2);
    const cleanSteps = steps.slice(
      0,
      getProcessStepIndex(ProcessStep.IN_PROCESS)
    );
    cleanSteps.push(...lastTwoValues);
    return cleanSteps;
  }
  return steps;
};

const filterIncomingSteps = steps => {
  return restoreStatesHandler(pausedStatesHandler(steps));
};

const stepsModifier = steps => {
  if (!steps?.length) return null;
  return printedStepsModifier(steps).map(stepObject => ({
    ...stepObject,
    ...(filterIncomingSteps(steps).find(
      inputStep => inputStep.step === stepObject.step
    ) || {}),
  }));
};

const currentActiveStep = (steps, currentStatusStep = {}) => {
  if (!steps?.length) return null;
  const lastStep = steps?.length - 1;
  return steps.reduce((acc, inputStep, index) => {
    if (
      inputStep.createdAt &&
      currentStatusStep?.step &&
      currentStatusStep.step === inputStep.step
    ) {
      acc = index;
    }
    return acc;
  }, lastStep);
};

const currentClassFormat = str =>
  `${str[0].toLowerCase()}${str.slice(1)}`.replace(/\s+/g, '');

const Orientation = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'))
    ? 'vertical'
    : 'horizontal';
};

const IsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

const getRestoreIcon = isMobile => (!isMobile ? <RestartAlt /> : null);
const getNextIcon = isMobile => (!isMobile ? <ArrowForward /> : null);
const getCancelIcon = isMobile => (!isMobile ? <Cancel /> : null);
const getPauseIcon = isMobile => (!isMobile ? <PauseCircleFilled /> : null);
const getResumeIcon = isMobile => (!isMobile ? <PlayCircle /> : null);

const StepperButtons = ({
  isVendor,
  isManager,
  isBilled,
  hasQualitySurvey,
  onNext,
  activeStep,
  currentLoadingButton,
  setCurrentLoadingButton,
  onCancel,
  onPause,
  onResume,
  onRestore,
  isApiLoading,
  isTechnicianAssigned = false,
  isOrderSigned = false,
  isSignOffEnabled = false,
}) => {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const buttonOptions = {
    restore: {
      order: 3,
      validation: {
        vendor: step =>
          step === ProcessStep.DONE && !hasQualitySurvey && !isBilled,
        dealer: step => false,
      },
      element: (
        <GenButton
          startIcon={getRestoreIcon(isMobile)}
          color="success"
          onClick={() => {
            onRestore();
            setCurrentLoadingButton('restore');
          }}
          disabled={
            activeStep.step === ProcessStep.PAUSED ||
            currentLoadingButton !== null
          }
          text={t('stepperButtons.restore')}
          loading={currentLoadingButton === 'restore' && isApiLoading === true}
        />
      ),
    },
    next: {
      order: 3,
      validation: {
        vendor: step =>
          step !== ProcessStep.GENERATED &&
          step !== ProcessStep.APPROVED &&
          step !== ProcessStep.DECLINED &&
          step !== ProcessStep.DONE &&
          step !== ProcessStep.CANCELED,
        dealer: step => false,
      },
      element: (
        <GenButton
          endIcon={getNextIcon(isMobile)}
          onClick={() => {
            onNext();
            if (
              activeStep.step === ProcessStep.IN_PROCESS &&
              isSignOffEnabled
            ) {
              if (isOrderSigned || isApiLoading) {
                setCurrentLoadingButton('next');
              }
            } else {
              setCurrentLoadingButton('next');
            }
          }}
          disabled={
            activeStep.step === ProcessStep.CANCELED ||
            activeStep.step === ProcessStep.PAUSED ||
            currentLoadingButton !== null ||
            isTechnicianAssigned
          }
          text={t('stepperButtons.next')}
          loading={currentLoadingButton === 'next' && isApiLoading === true}
        />
      ),
    },
    cancel: {
      order: 1,
      validation: {
        vendor: step =>
          step !== ProcessStep.DONE &&
          step !== ProcessStep.CANCELED &&
          step !== ProcessStep.DECLINED,

        dealer: step =>
          step === ProcessStep.GENERATED ||
          step === ProcessStep.IN_TRANSIT ||
          step === ProcessStep.ARRIVED,
      },
      element: (
        <GenButton
          startIcon={getCancelIcon(isMobile)}
          color="error"
          onClick={() => {
            onCancel();
            setCurrentLoadingButton('cancel');
          }}
          text={t('stepperButtons.cancel')}
          disabled={
            currentLoadingButton !== 'isVendorStoreManagerCancel' &&
            currentLoadingButton !== null
          }
          loading={
            currentLoadingButton === 'isVendorStoreManagerCancel' &&
            isApiLoading === true
          }
        />
      ),
    },
    pause: {
      order: 2,
      validation: {
        vendor: step =>
          step === ProcessStep.ARRIVED || step === ProcessStep.IN_PROCESS,
        dealer: step => false,
      },
      element: (
        <GenButton
          startIcon={getPauseIcon(isMobile)}
          color="warning"
          onClick={() => {
            onPause();
            setCurrentLoadingButton('pause');
          }}
          text={t('stepperButtons.pause')}
          disabled={
            currentLoadingButton !== 'pause' && currentLoadingButton !== null
          }
          loading={currentLoadingButton === 'pause' && isApiLoading === true}
        />
      ),
    },
    resume: {
      order: 2,
      validation: {
        vendor: step => step === ProcessStep.PAUSED,
        dealer: step => false,
      },
      element: (
        <GenButton
          startIcon={getResumeIcon(isMobile)}
          color="success"
          onClick={() => {
            onResume();
            setCurrentLoadingButton('resume');
          }}
          text={t('stepperButtons.resume')}
          disabled={
            currentLoadingButton !== 'resume' && currentLoadingButton !== null
          }
          loading={currentLoadingButton === 'resume' && isApiLoading === true}
        />
      ),
    },
  };

  const platform = isVendor ? 'vendor' : 'dealer';

  return (
    <Grid container>
      {Object.entries(buttonOptions)
        .filter(
          ([key, value]) =>
            isManager && value.validation[platform](activeStep.step)
        )
        .map(([key, value]) => (
          <Grid item key={key} sx={{ order: value.order }}>
            <Box sx={{ marginLeft: { xs: 1, sm: 2 } }}>{value.element}</Box>
          </Grid>
        ))}
    </Grid>
  );
};

const WorkOrderStepper = ({
  isVendor = false,
  isManager = false,
  isBilled = false,
  hasQualitySurvey,
  vendorOrderId,
  steps,
  statusChange,
  statusCancel,
  isApiLoading,
  detail = false,
  woNumber,
  isTechnicianAssigned = false,
  isSignOffEnabled = false,
  signoffDone = false,
  setOpenSignOffModal = () => {},
  isListViewLoading = false,
}) => {
  const { t } = useTranslation('workOrderStepper');
  const allSteps = stepsModifier(getStepsFromLastCreatedOrInitialSteps(steps));
  const lastStep = steps?.length ? steps[steps?.length - 1] : {};
  const [currentLoadingButton, setCurrentLoadingButton] = useState(null);
  const [isOrderSigned, setIsOrderSigned] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);

  useEffect(() => {
    if (isListViewLoading || isApiLoading) {
      setCurrentLoadingButton('next');
    } else {
      setCurrentLoadingButton(null);
    }

    if (!isApiLoading && !isSignOffEnabled) {
      setCurrentLoadingButton(null);
    }
    if (isSignOffEnabled) {
      if (signoffDone || isApiLoading) {
        setCurrentLoadingButton('next');
      } else {
        setCurrentLoadingButton(null);
      }
      setOpenSignOffModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApiLoading, signoffDone, isListViewLoading]);

  const onNext = () => {
    if (
      allSteps[currentActiveStep(allSteps, lastStep)].step ===
      ProcessStep.IN_PROCESS
    ) {
      if (signoffDone) {
        setIsOrderSigned(true);
      } else {
        setIsOrderSigned(false);
      }
      statusChange(ProcessStep.DONE, vendorOrderId);
    } else {
      statusChange(
        allSteps[currentActiveStep(allSteps, lastStep) + 1].step,
        vendorOrderId
      );
    }
  };

  const onCancel = () => {
    setCancelModalOpen(true);
  };

  const onPause = () => {
    setPauseModalOpen(true);
  };

  const onResume = () => {
    setResumeModalOpen(true);
  };

  const onRestore = () => {
    setRestoreModalOpen(true);
  };

  const handleActivityModalClose = () => {
    setActivityModalOpen(false);
  };

  const isMobile = IsMobile();
  return allSteps ? (
    <>
      <StyledGrid container flexDirection="column">
        <Grid item sx={{ flex: 1 }}>
          <Stepper
            activeStep={currentActiveStep(allSteps, lastStep)}
            orientation={Orientation()}
          >
            {allSteps.map(s => {
              return (
                <Step key={s.step}>
                  <StepLabel className={classes[currentClassFormat(s.step)]}>
                    <Box>
                      <Typography variant="body2" color="secondaryText">
                        {t(`workOrderSteps.${currentClassFormat(s.step)}`)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.disabled">
                        {fromDBToLocalTimestamp(
                          s?.createdAt,
                          DateTime.DATETIME_SHORT
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ maxWidth: '200px', whiteSpace: 'nowrap' }}>
                      <Box
                        component="div"
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          {[s?.person?.firstName, s?.person?.lastName]
                            .filter(x => x)
                            .join(' ')}
                        </Typography>
                      </Box>
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Grid>
        <Grid item>
          <Box px={1} pt={2}>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item>
                <GenButton
                  text={isMobile ? '' : t('wOStepperActivityPopUp.title')}
                  startIcon={<History />}
                  variant="outlined"
                  sx={{
                    color: theme => theme.palette.text.primary,
                    borderColor: theme => theme.palette.divider,
                    minHeight: '30px',
                    minWidth: '30px',
                    '& .MuiButton-startIcon': isMobile ? { margin: '0px' } : '',
                    '&:hover': {
                      borderColor: theme => theme.palette.divider,
                    },
                  }}
                  onClick={() => setActivityModalOpen(true)}
                />
              </Grid>
              <Grid item>
                <StepperButtons
                  isVendor={isVendor}
                  isManager={isManager}
                  isBilled={isBilled}
                  hasQualitySurvey={hasQualitySurvey}
                  activeStep={allSteps[currentActiveStep(allSteps, lastStep)]}
                  onNext={onNext}
                  onCancel={onCancel}
                  onPause={onPause}
                  onResume={onResume}
                  onRestore={onRestore}
                  isApiLoading={isApiLoading}
                  currentLoadingButton={currentLoadingButton}
                  setCurrentLoadingButton={setCurrentLoadingButton}
                  isTechnicianAssigned={isTechnicianAssigned}
                  isOrderSigned={isOrderSigned}
                  isSignOffEnabled={isSignOffEnabled}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </StyledGrid>
      <CancelModal
        open={cancelModalOpen}
        handleOpenState={setCancelModalOpen}
        vendorOrderId={vendorOrderId}
        statusCancel={statusCancel}
        isApiLoading={isApiLoading}
        setCurrentLoadingButton={setCurrentLoadingButton}
        woNumber={woNumber}
      />
      <PauseModal
        open={pauseModalOpen}
        handleOpenState={setPauseModalOpen}
        vendorOrderId={vendorOrderId}
        statusChange={statusChange}
        isApiLoading={isApiLoading}
        setCurrentLoadingButton={setCurrentLoadingButton}
      />
      <ResumeModal
        open={resumeModalOpen}
        vendorOrderId={vendorOrderId}
        handleOpenState={setResumeModalOpen}
        statusChange={statusChange}
        isApiLoading={isApiLoading}
        setCurrentLoadingButton={setCurrentLoadingButton}
      />
      <RestoreModal
        open={restoreModalOpen}
        vendorOrderId={vendorOrderId}
        handleOpenState={setRestoreModalOpen}
        statusChange={statusChange}
        isApiLoading={isApiLoading}
        setCurrentLoadingButton={setCurrentLoadingButton}
        woNumber={woNumber}
      />
      <GeneralModal
        modalState={activityModalOpen}
        forms={{
          activityLog: (
            <ActivityLogContentStructure
              steps={steps}
              closeModal={handleActivityModalClose}
            />
          ),
        }}
        initFormName="activityLog"
        handleModalState={setActivityModalOpen}
        closeModal={handleActivityModalClose}
      />
    </>
  ) : null;
};

export default WorkOrderStepper;
